import { useEffect, useMemo, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { format } from 'date-fns'
import { es as esDateFns } from 'date-fns/locale'
import { CalendarIcon, Info, Trash2 } from 'lucide-react'
import { DateTime } from 'luxon'
import { es as esRdp } from 'react-day-picker/locale'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { z } from 'zod'

import { Playlist } from '@/core/api/entities/playlist/playlist'
import { useTranslation } from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'
import { Button } from '@/shared/components/atoms/Button'
import { Checkbox } from '@/shared/components/atoms/Checkbox'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
} from '@/shared/components/atoms/Form'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/atoms/Popover'
import { ScrollArea } from '@/shared/components/atoms/Scroll'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/shared/components/molecules/AlertDialog'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { Calendar } from '@/shared/components/organisms/Calendar/Calendar'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

import { CalendarEvent } from '../../context/Data'
import { useEvents } from '../../context/EventContext'
import { usePlaylistData } from '../../hooks/usePlaylistData'
import { findOverlappingEvents } from '../../utils/priorityCalculator'
import { toLocalISOString } from '../../utils/shared'
import {
	generateSmartSlotEventGroup,
	getAllOccupancyLevels,
	OccupancyLevel,
	SmartSlotGroupConfig,
} from '../../utils/smartSlotConverter'
import { PlaylistSelector } from '../PlaylistSelector'
import { RepeatButton } from '../RepeatButton'
import { SmartSlotStepper } from './SmartSlotStepper'

const occupancyStepperSchema = z
	.object({
		date: z.date({
			message: "That's not a valid date!",
		}),
		customEndDate: z.boolean(),
		endDate: z.date().optional(),
		startTime: z.string().min(1, { message: 'Select a start time' }),
		endTime: z.string().min(1, { message: 'Select an end time' }),
		repeatDays: z.array(z.string()),
		playlists: z
			.array(z.number())
			.min(1, { message: 'Select at least 1 playlist' })
			.max(2, { message: 'Select a maximum of 2 playlists' }),
		playlistPercentages: z
			.record(z.string(), z.number().min(0).max(100))
			.optional(),
	})
	.refine(
		(data) => {
			if (data.customEndDate && !data.endDate) return false
			return true
		},
		{
			message: 'End date is required when custom end date is enabled',
			path: ['endDate'],
		}
	)

type OccupancyStepperFormValues = z.infer<typeof occupancyStepperSchema>

interface OccupancySmartSlotStepperProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	selectedDate: Date
	selectedStartTime: string
	selectedEndTime: string
	onComplete: (events: CalendarEvent[]) => void
	editingEvent?: CalendarEvent
	isDrag?: boolean
}

const generateTimeOptions = () => {
	const times: string[] = []
	for (let hour = 0; hour < 24; hour++) {
		for (let minute = 0; minute < 60; minute += 15) {
			const time24 = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
			times.push(time24)
		}
	}
	return times
}

const formatTime12Hour = (time24: string) => {
	const [hour] = time24.split(':').map(Number)
	const period = hour >= 12 ? 'PM' : 'AM'
	const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
	return `${displayHour} ${period}`
}

function calculateNextOccurrence(fromDate: Date, rrule: any): Date {
	const result = new Date(fromDate)

	if (!rrule || !rrule.byweekday) {
		result.setDate(result.getDate() + 1)
		return result
	}

	const validDays = new Set(
		Array.isArray(rrule.byweekday) ? rrule.byweekday : [rrule.byweekday]
	)

	for (let i = 1; i <= 7; i++) {
		const testDate = new Date(fromDate)
		testDate.setDate(testDate.getDate() + i)

		const dayOfWeek = testDate.getDay()
		const rruleDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1

		if (validDays.has(rruleDayOfWeek)) {
			return testDate
		}
	}

	result.setDate(result.getDate() + 1)
	return result
}

function generateTimeslotId(): string {
	return `timeslot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

export const OccupancySmartSlotStepper = ({
	open,
	onOpenChange,
	selectedDate,
	selectedStartTime,
	selectedEndTime,
	onComplete,
	editingEvent,
	isDrag = false,
}: OccupancySmartSlotStepperProps) => {
	const { t } = useTranslation()
	const { language } = useSelector((state: RootState) => state.ui)
	const { addEvent, updateEvent, deleteEvent, events } = useEvents()
	const { showNotification } = useNotificationContext()
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
	const [deleteOption, setDeleteOption] = useState<
		'this' | 'following' | 'all'
	>('this')
	const [selectedOccupancy, setSelectedOccupancy] =
		useState<OccupancyLevel>('low')
	const [completedLevels, setCompletedLevels] = useState<OccupancyLevel[]>([])
	// Store playlists per occupancy level
	const [playlistsByLevel, setPlaylistsByLevel] = useState<
		Record<
			OccupancyLevel,
			{ ids: number[]; percentages: Record<string, number> }
		>
	>({
		low: { ids: [], percentages: {} },
		medium: { ids: [], percentages: {} },
		high: { ids: [], percentages: {} },
	})
	// Track playlist objects across pagination/search to preserve names
	const [selectedPlaylistsMap, setSelectedPlaylistsMap] = useState<
		Record<number, Playlist>
	>({})

	// Get selected playlists for current occupancy level
	const selectedPlaylists = useMemo(() => {
		const fromMap = Object.values(selectedPlaylistsMap)

		// If we have playlists in the map, use those
		if (fromMap.length > 0) {
			return fromMap
		}

		// Otherwise, if we're editing and have playlist IDs, create stubs
		if (editingEvent && editingEvent.timeslotContents) {
			const stubs: Playlist[] = []
			editingEvent.timeslotContents.forEach((content) => {
				content.playlists.forEach((p) => {
					// Only add if not already in stubs
					if (!stubs.find((stub) => stub.id === p.id)) {
						stubs.push(
							Playlist.create({
								id: p.id,
								title: `Playlist ${p.id}`,
								percentage: p.percentage,
							})
						)
					}
				})
			})
			return stubs
		}

		return []
	}, [selectedPlaylistsMap, editingEvent])

	// Use shared playlist data hook with selected playlists
	const {
		allPlaylists,
		isLoading,
		isFetching,
		hasMorePages,
		loadMore,
		searchQuery,
		setSearchQuery,
		customPlaylistIds,
	} = usePlaylistData({
		dialogOpen: open,
		selectedPlaylists,
	})

	const form = useForm<OccupancyStepperFormValues>({
		resolver: zodResolver(occupancyStepperSchema),
		defaultValues: {
			playlists: [],
			customEndDate: false,
			repeatDays: [],
			playlistPercentages: {},
		},
	})

	const handleDeleteClick = () => {
		// If it's a single event (no repetition), delete directly without popup
		if (!editingEvent?.repeatDays || editingEvent.repeatDays.length === 0) {
			setDeleteOption('this')
			handleDelete()
		} else {
			// Show popup for recurring events
			setDeleteDialogOpen(true)
		}
	}

	const handleDelete = () => {
		if (!editingEvent) {
			return
		}

		// If it's not a recurring event, always treat it as 'this'
		const isRecurring =
			!!editingEvent.rrule ||
			(editingEvent.repeatDays && editingEvent.repeatDays.length > 0)
		const effectiveDeleteOption = isRecurring ? deleteOption : 'this'

		switch (effectiveDeleteOption) {
			case 'this':
				// Non-recurring or specifically 'this instance': delete normally
				if (!editingEvent?.rrule) {
					deleteEvent(editingEvent?.id || '')
					break
				}

				// Recurring: SPLIT THE TIMESLOT (same logic as EventEditForm)
				if (selectedDate && editingEvent?.rrule) {
					const deleteDate = new Date(selectedDate)
					const dtstartDate = new Date(editingEvent.rrule.dtstart)
					const hours = dtstartDate.getHours()
					const minutes = dtstartDate.getMinutes()
					const seconds = dtstartDate.getSeconds()

					deleteDate.setHours(hours, minutes, seconds, 0)

					const dayBeforeDelete = new Date(deleteDate)
					dayBeforeDelete.setDate(dayBeforeDelete.getDate() - 1)
					dayBeforeDelete.setHours(23, 59, 59, 999)

					const nextOccurrence = calculateNextOccurrence(
						deleteDate,
						editingEvent.rrule
					)
					nextOccurrence.setHours(hours, minutes, seconds, 0)

					// Clean rrule object - only keep essential properties
					const cleanRRule = {
						freq: editingEvent.rrule.freq,
						...(editingEvent.rrule.byweekday
							? { byweekday: editingEvent.rrule.byweekday }
							: {}),
						...(editingEvent.rrule.until &&
						editingEvent.rrule.until !== toLocalISOString(dayBeforeDelete)
							? { until: editingEvent.rrule.until }
							: {}),
					}

					const timeslot1: CalendarEvent = {
						...editingEvent,
						id: generateTimeslotId(),
						timeslotId: undefined, // Clear these so they are treated as new slots
						timeslotContentId: undefined,
						rrule: {
							...cleanRRule,
							dtstart: editingEvent.rrule.dtstart,
							until: toLocalISOString(dayBeforeDelete),
						},
					}

					const timeslot2: CalendarEvent = {
						...editingEvent,
						id: generateTimeslotId(),
						timeslotId: undefined, // Clear these so they are treated as new slots
						timeslotContentId: undefined,
						rrule: {
							...cleanRRule,
							dtstart: toLocalISOString(nextOccurrence),
							// Keep original until if it existed
							...(editingEvent.rrule.until
								? { until: editingEvent.rrule.until }
								: {}),
						},
					}

					deleteEvent(editingEvent.id)

					// Only add the first part if it has at least one occurrence
					// (dayBeforeDelete must be >= original dtstart)
					if (dayBeforeDelete.getTime() >= dtstartDate.getTime()) {
						addEvent(timeslot1)
					}

					// Only add the second part if it hasn't passed the original UNTIL date
					let isPart2Valid = true
					if (editingEvent.rrule.until) {
						const originalUntilDate = new Date(editingEvent.rrule.until)
						if (nextOccurrence.getTime() > originalUntilDate.getTime()) {
							isPart2Valid = false
						}
					}

					if (isPart2Valid) {
						addEvent(timeslot2)
					}

					showNotification({
						type: 'success',
						message: 'Occupancy slot instance excluded',
						description:
							'The smart slot has been split to exclude this occurrence.',
					})
				}
				break
			case 'following':
				// Delete this and following events by setting until date in rrule
				if (editingEvent.start && editingEvent?.rrule) {
					const untilDate = new Date(editingEvent.start)
					untilDate.setDate(untilDate.getDate() - 1) // Set until to day before this event

					// If the until date is before the original start of the recurrence,
					// it means we are deleting everything, so just delete the event.
					const recurrenceStart = new Date(editingEvent.rrule.dtstart)
					if (untilDate.getTime() < recurrenceStart.getTime()) {
						deleteEvent(editingEvent.id)
						break
					}

					// Format until date as ISO string
					const formattedUntil = toLocalISOString(untilDate)

					// Delete and re-add the event to force FullCalendar update
					deleteEvent(editingEvent.id)
					const updatedEvent = {
						...editingEvent,
						rrule: {
							...editingEvent.rrule,
							until: formattedUntil,
						},
					}

					addEvent(updatedEvent)
				}
				break
			case 'all':
				deleteEvent(editingEvent.id)
				break
		}

		setDeleteDialogOpen(false)
		onOpenChange(false)
	}

	useEffect(() => {
		if (open) {
			const startTime = `${String(selectedDate.getHours()).padStart(2, '0')}:${String(selectedDate.getMinutes()).padStart(2, '0')}`
			const endTime = `${String(selectedEndTime.split(':')[0]).padStart(2, '0')}:${String(selectedEndTime.split(':')[1]).padStart(2, '0')}`

			// If editing an existing smart slot, load its data
			if (editingEvent && editingEvent.timeslotContents) {
				// Build playlists by level from timeslotContents
				const newPlaylistsByLevel: Record<
					OccupancyLevel,
					{ ids: number[]; percentages: Record<string, number> }
				> = {
					low: { ids: [], percentages: {} },
					medium: { ids: [], percentages: {} },
					high: { ids: [], percentages: {} },
				}

				// Map timeslotContents to occupancy levels by index order
				// First item = low, second = medium, third = high
				const levelOrder: OccupancyLevel[] = ['low', 'medium', 'high']
				editingEvent.timeslotContents.forEach((content, index) => {
					const occupancyLevel = levelOrder[index] as OccupancyLevel
					newPlaylistsByLevel[occupancyLevel] = {
						ids: content.playlists.map((p) => p.id),
						percentages: Object.fromEntries(
							content.playlists.map((p) => [p.id.toString(), p.percentage])
						),
					}
				})

				setPlaylistsByLevel(newPlaylistsByLevel)
				setSelectedOccupancy('low')
				setCompletedLevels([]) // Reset for re-editing

				// Load playlists for low level immediately
				const lowLevelData = newPlaylistsByLevel['low']

				// For recurring events, use the original rrule date, not the instance date
				let formDate = editingEvent.start
				if (editingEvent.rrule && editingEvent.rrule.dtstart) {
					formDate = new Date(editingEvent.rrule.dtstart)
				}

				// Check if the event has a custom end date (has until in rrule)
				let hasCustomEndDate = false
				let endDateValue: Date | undefined = undefined
				if (editingEvent.rrule && editingEvent.rrule.until) {
					hasCustomEndDate = true
					endDateValue = new Date(editingEvent.rrule.until)
				}

				form.reset({
					date: formDate,
					customEndDate: hasCustomEndDate,
					endDate: endDateValue,
					startTime: startTime,
					endTime: endTime,
					repeatDays: editingEvent.repeatDays || [],
					playlists: lowLevelData.ids,
					playlistPercentages: lowLevelData.percentages,
				})
			} else {
				// Creating new slot
				form.reset({
					date: selectedDate,
					customEndDate: false,
					endDate: undefined,
					startTime: startTime,
					endTime: endTime,
					repeatDays: [],
					playlists: [],
					playlistPercentages: {},
				})
				setPlaylistsByLevel({
					low: { ids: [], percentages: {} },
					medium: { ids: [], percentages: {} },
					high: { ids: [], percentages: {} },
				})
				setSelectedOccupancy('low')
				setCompletedLevels([])
			}
		}
	}, [
		open,
		selectedDate,
		selectedEndTime,
		selectedStartTime,
		editingEvent,
		form,
	])

	// Load playlists for the current level when selectedOccupancy changes
	useEffect(() => {
		const levelData = playlistsByLevel[selectedOccupancy]
		if (levelData) {
			form.setValue('playlists', levelData.ids)
			form.setValue('playlistPercentages', levelData.percentages)
		}
	}, [selectedOccupancy])

	// Auto-save changes to playlistsByLevel when form values change
	const currentPlaylists = form.watch('playlists')
	const currentPercentages = form.watch('playlistPercentages')

	useEffect(() => {
		setPlaylistsByLevel((prev) => ({
			...prev,
			[selectedOccupancy]: {
				ids: currentPlaylists || [],
				percentages: currentPercentages || {},
			},
		}))
	}, [currentPlaylists, currentPercentages, selectedOccupancy])

	const handleSubmit = async (data: OccupancyStepperFormValues) => {
		// Validate that current level has at least one playlist
		if (data.playlists.length === 0) {
			showNotification({
				message: `Please select at least one playlist for ${selectedOccupancy}`,
				type: 'error',
			})
			return
		}

		// Save playlists for current level FIRST
		const updatedPlaylistsByLevel = {
			...playlistsByLevel,
			[selectedOccupancy]: {
				ids: data.playlists,
				percentages: data.playlistPercentages || {},
			},
		}
		setPlaylistsByLevel(updatedPlaylistsByLevel)

		// Only add to completedLevels if not already there
		const newCompletedLevels = completedLevels.includes(selectedOccupancy)
			? completedLevels
			: [...completedLevels, selectedOccupancy]
		setCompletedLevels(newCompletedLevels)

		// Check if all occupancy levels are done
		const allLevels = getAllOccupancyLevels()
		const remainingLevels = allLevels.filter(
			(l) => !newCompletedLevels.includes(l)
		)

		if (remainingLevels.length > 0) {
			// Move to next level
			const nextLevel = remainingLevels[0]
			setSelectedOccupancy(nextLevel)

			// Get the playlists for the next level if they exist
			const nextLevelData = updatedPlaylistsByLevel[nextLevel]
			const nextLevelPlaylists = nextLevelData?.ids || []
			const nextLevelPercentages = nextLevelData?.percentages || {}

			// Reset form for next level with previously saved playlists if available
			form.reset({
				playlists: nextLevelPlaylists,
				customEndDate: data.customEndDate,
				repeatDays: data.repeatDays,
				playlistPercentages: nextLevelPercentages,
				date: data.date,
				startTime: data.startTime,
				endTime: data.endTime,
				endDate: data.endDate,
			})
		} else {
			// All levels done - create single event with all levels as contents
			const playlistsByLevelSnapshot = updatedPlaylistsByLevel
			const occupancyLevelRanges: Record<string, { min: number; max: number }> =
				{
					low: { min: 0, max: 33 },
					medium: { min: 34, max: 66 },
					high: { min: 67, max: 100 },
				}

			const contents = allLevels.map((level) => {
				const levelData = playlistsByLevelSnapshot[level]
				const range = occupancyLevelRanges[level]

				return {
					type: 'occupation',
					volume: 100, // Occupancy slots always use volume 100
					min_percentage: range.min,
					max_percentage: range.max,
					playlists: levelData.ids.map((id) => {
						const percentage =
							levelData.percentages?.[id] ||
							Math.floor(100 / levelData.ids.length)
						return {
							id: id,
							percentage: percentage,
						}
					}),
				}
			}) as Array<{
				type: string
				volume: number
				min_percentage: number
				max_percentage: number
				playlists: Array<{ id: number; percentage: number }>
			}>

			const allPlaylistIdsSet = new Set<number>()
			contents.forEach((content) => {
				content.playlists.forEach((p) => allPlaylistIdsSet.add(p.id))
			})

			const allPlaylistIds = Array.from(allPlaylistIdsSet)
			const allUniquePlaylistNames = allPlaylistIds
				.map((id) => selectedPlaylistsMap[id]?.title)
				.filter(Boolean) as string[]

			// For title: use first playlist from first level
			const firstContent = contents[0]
			const firstPlaylistName =
				firstContent && firstContent.playlists[0]
					? selectedPlaylistsMap[firstContent.playlists[0].id]?.title ||
						'Slot 1'
					: 'Slot 1'

			const groupConfig: SmartSlotGroupConfig = {
				id: editingEvent?.id,
				name: firstPlaylistName, // Use first playlist name
				startDate: data.date,
				endDate: data.endDate || data.date,
				startTime: data.startTime,
				endTime: data.endTime,
				repeatDays: data.repeatDays,
				customEndDate: data.customEndDate,
				endDateCustom: data.endDate,
				variant: 'occupation',
				playlistNames: allUniquePlaylistNames, // Pass ALL unique playlist names for gradient
				contents: contents,
			}

			const event = generateSmartSlotEventGroup(groupConfig, t)

			// Overlap check
			const overlaps = findOverlappingEvents(
				{
					start: event.start,
					end: event.end,
					repeatDays: event.repeatDays,
					rrule: (event as any).rrule,
				},
				events,
				editingEvent?.id
			)

			if (overlaps.length > 0) {
				showNotification({
					type: 'error',
					message: t('entities.schedule.calendar.multiple_overlaps_detected'),
					description: t('entities.schedule.calendar.overlap_description'),
				})
				return
			}

			// If editing, update the event; otherwise add new event
			if (editingEvent) {
				// Preserve the original dtstart date if editing a recurring event
				const originalEventFromContext = events.find(
					(e) => e.id === editingEvent.id
				)

				if (
					originalEventFromContext?.timeslotContents &&
					event.timeslotContents
				) {
					event.timeslotContents = event.timeslotContents.map((newContent) => {
						const originalContent =
							originalEventFromContext.timeslotContents?.find(
								(oc) => oc.type === newContent.type
							)
						return {
							...newContent,
							...(originalContent?.id ? { id: originalContent.id } : {}),
						}
					})
				}

				if ((originalEventFromContext as any)?.timeslotId) {
					;(event as any).timeslotId = (
						originalEventFromContext as any
					).timeslotId
				}

				if (
					isDrag &&
					originalEventFromContext?.rrule &&
					originalEventFromContext.rrule.dtstart &&
					event.rrule
				) {
					const originalDtstart = new Date(
						originalEventFromContext.rrule.dtstart
					)
					const newDtstart = new Date(originalDtstart)

					// Update only the time, keep the date
					const [newHour, newMinute] = data.startTime.split(':').map(Number)
					newDtstart.setHours(newHour, newMinute, 0, 0)

					event.rrule.dtstart = toLocalISOString(newDtstart)
				}

				if (
					originalEventFromContext?.rrule &&
					originalEventFromContext.rrule.until &&
					event.rrule &&
					!data.customEndDate
				) {
					event.rrule.until = originalEventFromContext.rrule.until
				}

				updateEvent(editingEvent.id, event)
			} else {
				addEvent(event)
			}

			onOpenChange(false)
			onComplete([event])
		}
	}

	const getStepTitle = () => {
		const allLevels = getAllOccupancyLevels()
		const remaining = allLevels.filter(
			(l) => !completedLevels.includes(l)
		).length
		const total = allLevels.length
		const current = total - remaining + 1

		return `${t('entities.schedule.smart_slot.occupancy_smart_slot_title')} (${current}/${total})`
	}

	const getDateTimeDisplay = () => {
		const date = form.getValues('date')
		const startTime = form.getValues('startTime')
		const endTime = form.getValues('endTime')

		if (date && startTime && endTime) {
			return `${format(date, 'EEEE, d MMM')} from ${formatTime12Hour(startTime)} to ${formatTime12Hour(endTime)}`
		}
		return 'Select date and time'
	}

	return (
		<>
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent className="max-w-2xl p-0 gap-0 flex flex-col">
					<DialogHeader className="px-6 pt-6 pb-4">
						<DialogTitle className="text-xl">{getStepTitle()}</DialogTitle>
						<DialogDescription className="text-sm text-muted-foreground mt-2">
							{getDateTimeDisplay()}
						</DialogDescription>
					</DialogHeader>

					<Separator />

					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(handleSubmit)}
							className="flex flex-col"
						>
							<div className="px-6 py-4 space-y-6">
								{/* DATE & TIME SECTION */}
								<div className="space-y-3">
									{/* Date and Time in one row */}
									<div className="flex items-center gap-2 text-sm">
										{/* Date Picker */}
										<FormField
											control={form.control}
											name="date"
											render={({ field }) => (
												<FormItem>
													<Popover>
														<PopoverTrigger asChild>
															<FormControl>
																<Button
																	variant="ghost"
																	className="h-auto p-0 hover:bg-transparent font-normal"
																>
																	{field.value ? (
																		format(field.value, 'EEEE, d MMM', {
																			locale: language?.startsWith('es')
																				? esDateFns
																				: undefined,
																		})
																	) : (
																		<span>
																			{t(
																				'entities.schedule.slot_form.pick_a_date'
																			)}
																		</span>
																	)}
																</Button>
															</FormControl>
														</PopoverTrigger>
														<PopoverContent
															className="w-auto p-0"
															align="start"
														>
															<Calendar
																mode="single"
																selected={field.value}
																onSelect={field.onChange}
																autoFocus
																locale={
																	language?.startsWith('es') ? esRdp : undefined
																}
															/>
														</PopoverContent>
													</Popover>
												</FormItem>
											)}
										/>

										<span className="text-muted-foreground">from</span>

										{/* Start Time */}
										<FormField
											control={form.control}
											name="startTime"
											render={({ field }) => (
												<FormItem>
													<Select
														onValueChange={field.onChange}
														defaultValue={field.value}
														value={field.value}
													>
														<FormControl>
															<SelectTrigger className="h-auto px-2 py-1 w-auto border-0 bg-muted/50">
																<SelectValue placeholder="Start">
																	{field.value && formatTime12Hour(field.value)}
																</SelectValue>
															</SelectTrigger>
														</FormControl>
														<SelectContent>
															<ScrollArea className="h-60">
																{generateTimeOptions().map((time) => (
																	<SelectItem key={time} value={time}>
																		{formatTime12Hour(time)}
																	</SelectItem>
																))}
															</ScrollArea>
														</SelectContent>
													</Select>
												</FormItem>
											)}
										/>

										<span className="text-muted-foreground">to</span>

										{/* End Time */}
										<FormField
											control={form.control}
											name="endTime"
											render={({ field }) => (
												<FormItem>
													<Select
														onValueChange={field.onChange}
														defaultValue={field.value}
														value={field.value}
													>
														<FormControl>
															<SelectTrigger className="h-auto px-2 py-1 w-auto border-0 bg-muted/50">
																<SelectValue placeholder="End">
																	{field.value &&
																		DateTime.fromFormat(
																			field.value,
																			'HH:mm'
																		).toLocaleString(DateTime.TIME_SIMPLE)}
																</SelectValue>
															</SelectTrigger>
														</FormControl>
														<SelectContent>
															<ScrollArea className="h-60">
																{generateTimeOptions().map((time) => (
																	<SelectItem key={time} value={time}>
																		{DateTime.fromFormat(
																			time,
																			'HH:mm'
																		).toLocaleString(DateTime.TIME_SIMPLE)}
																	</SelectItem>
																))}
															</ScrollArea>
														</SelectContent>
													</Select>
												</FormItem>
											)}
										/>
									</div>

									{/* Second row: Checkbox and Repeat button */}
									<div className="flex items-center justify-between">
										{/* Repeat Button */}
										<FormField
											control={form.control}
											name="repeatDays"
											render={({ field }) => (
												<FormItem>
													<RepeatButton
														value={field.value}
														onChange={field.onChange}
													/>
												</FormItem>
											)}
										/>

										{/* Custom End Date Checkbox - only shown when repeat is activated */}
										{form.watch('repeatDays').length > 0 && (
											<FormField
												control={form.control}
												name="customEndDate"
												render={({ field }) => (
													<FormItem className="flex flex-row items-center space-x-2 space-y-0">
														<FormControl>
															<Checkbox
																checked={field.value}
																onCheckedChange={field.onChange}
															/>
														</FormControl>
														<FormLabel className="text-sm font-normal cursor-pointer">
															Choose custom end date
														</FormLabel>
													</FormItem>
												)}
											/>
										)}
									</div>

									{/* End Date Picker (shown when custom end date is checked) */}
									{form.watch('customEndDate') && (
										<FormField
											control={form.control}
											name="endDate"
											render={({ field }) => (
												<FormItem className="flex flex-col">
													<Popover>
														<PopoverTrigger asChild>
															<FormControl>
																<Button
																	variant="outline"
																	className="w-full justify-start text-left font-normal h-9 text-sm"
																>
																	<CalendarIcon className="mr-2 h-4 w-4" />
																	{field.value ? (
																		format(field.value, 'EEEE, d MMM', {
																			locale: language?.startsWith('es')
																				? esDateFns
																				: undefined,
																		})
																	) : (
																		<span>
																			{t(
																				'entities.schedule.slot_form.pick_a_date'
																			)}
																		</span>
																	)}
																</Button>
															</FormControl>
														</PopoverTrigger>
														<PopoverContent
															className="w-auto p-0"
															align="start"
														>
															<Calendar
																mode="single"
																selected={field.value}
																onSelect={field.onChange}
																autoFocus
																locale={
																	language?.startsWith('es') ? esRdp : undefined
																}
															/>
														</PopoverContent>
													</Popover>
												</FormItem>
											)}
										/>
									)}
								</div>

								{/* Stepper Indicator */}
								<SmartSlotStepper
									type="occupation"
									activeStep={
										['low', 'medium', 'high'].indexOf(selectedOccupancy) + 1
									}
									onStepChange={(step) => {
										const levels: OccupancyLevel[] = ['low', 'medium', 'high']
										setSelectedOccupancy(levels[step - 1])
									}}
									completedSteps={completedLevels.map(
										(level) => ['low', 'medium', 'high'].indexOf(level) + 1
									)}
									canChangeStep={(nextStep) => {
										const currentStepNumber =
											['low', 'medium', 'high'].indexOf(selectedOccupancy) + 1
										const completedStepNumbers = completedLevels.map(
											(level) => ['low', 'medium', 'high'].indexOf(level) + 1
										)

										// Allow moving backwards to completed steps
										if (
											nextStep < currentStepNumber &&
											completedStepNumbers.includes(nextStep)
										) {
											return true
										}

										// Allow moving forward only if current step has at least one playlist
										if (nextStep > currentStepNumber) {
											return form.watch('playlists').length > 0
										}

										// Allow staying on same step or moving to completed steps
										return nextStep <= currentStepNumber
									}}
								/>

								{/* PLAYLIST SELECTION SECTION */}
								<FormField
									control={form.control}
									name="playlists"
									render={({ field }) => (
										<FormItem>
											<PlaylistSelector
												value={field.value}
												onChange={field.onChange}
												onPercentagesChange={(percentages) =>
													form.setValue('playlistPercentages', percentages)
												}
												onPlaylistsChange={(playlists) => {
													setSelectedPlaylistsMap((prev) => {
														const next = { ...prev }
														playlists.forEach((p) => {
															next[p.id] = p
														})
														return next
													})
												}}
												playlists={allPlaylists}
												isLoading={isLoading || isFetching}
												hasMorePages={hasMorePages}
												onLoadMore={loadMore}
												dialogOpen={open}
												searchQuery={searchQuery}
												onSearchQueryChange={setSearchQuery}
												customPlaylistIds={customPlaylistIds}
											/>
										</FormItem>
									)}
								/>

								{/* PLAYBACK PERCENTAGE SECTION */}
								{form.watch('playlists').length === 2 && (
									<div>
										<div className="space-y-2">
											<div className="flex items-center gap-2">
												<h4 className="text-sm font-medium">
													Playback percentage
												</h4>
												<Info className="h-3 w-3 text-muted-foreground" />
											</div>
											<div className="flex gap-2">
												{form.watch('playlists').map((playlistId) => {
													const playlist = allPlaylists.find(
														(p) => p.id === playlistId
													)
													if (!playlist) return null

													const currentPercentages =
														form.watch('playlistPercentages') || {}
													const percentage =
														currentPercentages[playlistId] ||
														Math.floor(100 / form.watch('playlists').length)

													return (
														<div
															key={playlistId}
															className="flex-1 flex items-center gap-2 px-2 py-1.5 border rounded-lg bg-muted/50"
														>
															<span
																className="text-xs font-medium flex-1 truncate max-w-[50px]"
																title={playlist.title}
															>
																{playlist.title}
															</span>
															{/**Commented out because we don't need to show the playback percentage selector in the event edit form, because the user won't be able to change it.
															 * <button
																type="button"
																onClick={() => {
																	const newPercentages = {
																		...currentPercentages,
																	}
																	const otherPlaylistIds = form
																		.watch('playlists')
																		.filter((id) => id !== playlistId)
																	const newValue = Math.max(0, percentage - 10)
																	newPercentages[playlistId] = newValue

																	let remainingPercentage = 100 - newValue
																	otherPlaylistIds.forEach((id) => {
																		newPercentages[id] = Math.floor(
																			remainingPercentage /
																				otherPlaylistIds.length
																		)
																	})
																	form.setValue(
																		'playlistPercentages',
																		newPercentages
																	)
																}}
																disabled={true}
																className="h-5 w-5 rounded-full border flex items-center justify-center hover:bg-background transition-colors flex-shrink-0 cursor-not-allowed"
															>
																<Minus className="h-3 w-3" />
															</button>
															*/}
															<span className="text-xs font-semibold w-10 text-center flex-shrink-0">
																{percentage}%
															</span>
															{/**
															 * <button
																type="button"
																onClick={() => {
																	const newPercentages = {
																		...currentPercentages,
																	}
																	const otherPlaylistIds = form
																		.watch('playlists')
																		.filter((id) => id !== playlistId)
																	const newValue = Math.min(
																		100,
																		percentage + 10
																	)
																	newPercentages[playlistId] = newValue

																	let remainingPercentage = 100 - newValue
																	otherPlaylistIds.forEach((id) => {
																		newPercentages[id] = Math.floor(
																			remainingPercentage /
																				otherPlaylistIds.length
																		)
																	})
																	form.setValue(
																		'playlistPercentages',
																		newPercentages
																	)
																}}
																disabled={true}
																className="h-5 w-5 rounded-full border flex items-center justify-center hover:bg-background transition-colors flex-shrink-0 cursor-not-allowed"
															>
																<Plus className="h-3 w-3" />
															</button>
															*/}
														</div>
													)
												})}
											</div>
										</div>
									</div>
								)}
							</div>

							<Separator />

							{/* Footer Buttons */}
							<DialogFooter className="px-6 py-4 mt-auto flex justify-between">
								{editingEvent && (
									<Button
										type="button"
										variant="destructive"
										onClick={handleDeleteClick}
										className="mr-auto h-8 rounded-full px-6"
									>
										<Trash2 className="h-4 w-4 mr-2" />
										{t('entities.schedule.slot_form.delete_slot')}
									</Button>
								)}
								<div className="flex gap-2">
									<Button
										type="button"
										variant="outline"
										onClick={() => onOpenChange(false)}
										className="h-8 rounded-full px-6"
									>
										{t('actions.cancel')}
									</Button>
									<Button
										type="submit"
										className="h-8 rounded-full px-6"
										disabled={form.watch('playlists').length === 0}
										title={
											form.watch('playlists').length === 0
												? t(
														'entities.schedule.smart_slot.select_playlist_for',
														{ condition: selectedOccupancy }
													)
												: ''
										}
									>
										{t('actions.continue')}
									</Button>
								</div>
							</DialogFooter>
						</form>
					</Form>
				</DialogContent>
			</Dialog>

			{/* Delete Confirmation Dialog */}
			<AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t('entities.schedule.delete_dialog.title')}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t('entities.schedule.delete_dialog.description')}
						</AlertDialogDescription>
					</AlertDialogHeader>

					<div className="py-4 space-y-3">
						<div
							className="flex items-center space-x-3 cursor-pointer"
							onClick={() => setDeleteOption('this')}
						>
							<div
								className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${deleteOption === 'this' ? 'border-primary' : 'border-muted-foreground'}`}
							>
								{deleteOption === 'this' && (
									<div className="w-2.5 h-2.5 rounded-full bg-primary" />
								)}
							</div>
							<span className="font-normal">
								{t('entities.schedule.delete_dialog.this_event')}
							</span>
						</div>
						<div
							className="flex items-center space-x-3 cursor-pointer"
							onClick={() => setDeleteOption('following')}
						>
							<div
								className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${deleteOption === 'following' ? 'border-primary' : 'border-muted-foreground'}`}
							>
								{deleteOption === 'following' && (
									<div className="w-2.5 h-2.5 rounded-full bg-primary" />
								)}
							</div>
							<span className="font-normal">
								{t('entities.schedule.delete_dialog.this_and_following')}
							</span>
						</div>
						<div
							className="flex items-center space-x-3 cursor-pointer"
							onClick={() => setDeleteOption('all')}
						>
							<div
								className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${deleteOption === 'all' ? 'border-primary' : 'border-muted-foreground'}`}
							>
								{deleteOption === 'all' && (
									<div className="w-2.5 h-2.5 rounded-full bg-primary" />
								)}
							</div>
							<span className="font-normal">
								{t('entities.schedule.delete_dialog.all_events')}
							</span>
						</div>
					</div>

					<AlertDialogFooter>
						<AlertDialogCancel className="h-8 rounded-full px-6">
							{t('actions.back')}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDelete}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90 h-8 rounded-full px-6"
						>
							{t('actions.delete')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
